import * as React from 'react';
import { usePortalLocale } from '@atlassian/help-center-common-util/portal-locale';

interface LocaleAwareProps {
	locale: string;
}

/**
 * Replaces the `locale` prop with the portal's locale for components rendered
 * inside `PortalIntlBoundary`.
 *
 * Applied at the leaf rather than threaded from each page so that every call
 * site is covered at once. Outside a portal page the context is `undefined` and
 * the incoming prop — the account locale, supplied by Redux further up — is used
 * unchanged, which is what keeps embedded and non-portal surfaces on the user's
 * own locale.
 */
export const withPortalLocale = <TProps extends LocaleAwareProps>(
	LocaleAwareComponent: React.ComponentType<TProps>,
) => {
	const WithPortalLocale = (props: TProps) => {
		const portalLocale = usePortalLocale();

		return <LocaleAwareComponent {...props} locale={portalLocale ?? props.locale} />;
	};

	WithPortalLocale.displayName = `withPortalLocale(${
		LocaleAwareComponent.displayName || LocaleAwareComponent.name || 'Component'
	})`;

	return WithPortalLocale;
};
